import React, { useState, useEffect } from "react";
import { CardHeader, IconButton, Chip, Tooltip } from "@material-ui/core";
import WhatsAppAvatar from "../WhatsAppAvatar";
import { Person, PersonAddOutlined, LocalShippingOutlined, Info as InfoIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import api from "../../services/api";
import ClientEditDialog from "../../pages/Clients/ClientEditDialog";
import { formatContactName, formatPhoneNumber } from "../../utils/formatPhoneNumber";
import { getOrderStatusChipLabel } from "../../utils/orderStatusLabels";
import OrderDetailsDialog from "../OrderDetailsDialog";
const useStyles = makeStyles((theme) => ({
    buttonContainer: {
        display: 'flex',
        gap: '8px',
        marginRight: 16,
        flexShrink: 0,
    },
    clientButton: {
        width: 35,
        height: 35,
        borderRadius: '50%',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'scale(1.1)',
        }
    },
    commentButton: {
        width: 35,
        height: 35,
        borderRadius: '50%',
        backgroundColor: '#FFA500',
        color: '#fff',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: '#FF8C00',
            transform: 'scale(1.1)',
        }
    },
    existingClient: {
        backgroundColor: '#50C878',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#45B069',
        }
    },
    newClient: {
        backgroundColor: '#FF6B6B',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#FF5252',
        }
    },
    packagesButton: {
        width: 35,
        height: 35,
        borderRadius: '50%',
        backgroundColor: '#4A90E2',
        color: '#fff',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: '#357ABD',
            transform: 'scale(1.1)',
        }
    },
    headerContainer: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0, // Enable text truncation
    },
    cardHeaderRoot: {
        flex: 1,
        minWidth: 0, // Enable text truncation
        padding: '16px 8px', // Reduce padding to give more space
    },
    titleText: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    commentDialog: {
        padding: theme.spacing(2),
    },
    commentDialogTitle: {
        backgroundColor: '#FFA500',
        color: '#fff',
    },
    commentContent: {
        padding: theme.spacing(2),
        minWidth: 300,
        maxWidth: 500,
    },
    orderChip: {
        marginLeft: 4,
        fontSize: '0.7rem',
    },
    orderChipActive: {
        backgroundColor: '#2196F3',
        color: '#fff',
    },
    orderChipFinalized: {
        backgroundColor: '#4CAF50',
        color: '#fff',
    },
}));

function mapAllexpressClientToForm(client) {
    if (!client) return null;
    const pn = client.phoneNumber;
    const fullPhone = pn ? `${pn.countryCode || ''}${pn.phoneNumber || ''}` : '';
    return {
        id: client.id,
        first_name: client.name || '',
        last_names: client.lastName || '',
        phone: fullPhone,
        notes: client.notes || '',
    };
}

const TicketInfo = ({ contact, ticket, onClick }) => {
    const classes = useStyles();
    const [clientData, setClientData] = useState(null);
    const [showClientDialog, setShowClientDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [clientFirstName, setClientFirstName] = useState("");
    const [clientLastName, setClientLastName] = useState("");
    const [orderStatus, setOrderStatus] = useState(null);
    const [trackingBaseUrl, setTrackingBaseUrl] = useState("");
    const [showOrderDetails, setShowOrderDetails] = useState(false);

    useEffect(() => {
        const fetchClient = async () => {
            if (!contact?.number) return;
            setLoading(true);
            try {
                const { data } = await api.get('api/allexpress/clients/by-phone', {
                    params: { phone: contact.number }
                });
                if (data?.data) {
                    setClientData(mapAllexpressClientToForm(data.data));
                } else {
                    setClientData(null);
                    const names = (contact.name || '').split(" ");
                    setClientFirstName(names[0] || "");
                    setClientLastName(names[1] || "");
                }
            } catch (err) {
                if (err.response?.status !== 404) {
                    toast.error('Error al buscar cliente en Allexpress');
                }
                setClientData(null);
                const names = (contact.name || '').split(" ");
                setClientFirstName(names[0] || "");
                setClientLastName(names[1] || "");
            } finally {
                setLoading(false);
            }
        };
        fetchClient();
    }, [contact?.number]);

    useEffect(() => {
        if (!clientData?.id) {
            setOrderStatus(null);
            return;
        }
        let cancelled = false;
        const fetchOrderStatus = async () => {
            try {
                const { data } = await api.get('api/allexpress/orders/status', {
                    params: { clientId: clientData.id }
                });
                if (!cancelled && data?.data) setOrderStatus(data.data);
            } catch {
                if (!cancelled) setOrderStatus(null);
            }
        };
        fetchOrderStatus();
        return () => { cancelled = true; };
    }, [clientData?.id]);

    useEffect(() => {
        api.get('api/allexpress/tracking-base-url').then(({ data }) => {
            if (data?.data?.baseUrl) setTrackingBaseUrl(data.data.baseUrl);
        }).catch(() => {});
    }, []);

    const handleClientButton = () => {
        setShowClientDialog(true);
    };

    const handleSaveClient = async (formData) => {
        try {
            const payload = {
                name: formData.first_name?.trim() || '',
                lastName: formData.last_names?.trim() || '',
                phone: formData.phone?.trim() || '',
                notes: formData.notes || '',
            };
            if (formData.id) {
                await api.patch(`api/allexpress/clients/${formData.id}`, payload);
                toast.success("Cliente actualizado correctamente");
            } else {
                await api.post('api/allexpress/clients', payload);
                toast.success("Cliente creado correctamente");
            }
            const res = await api.get('api/allexpress/clients/by-phone', {
                params: { phone: contact.number }
            });
            if (res.data?.data) {
                setClientData(mapAllexpressClientToForm(res.data.data));
            }
            setShowClientDialog(false);
        } catch (err) {
            if (err.response?.status === 409) {
                toast.error('Ya existe un cliente con este número de teléfono');
            } else {
                toast.error(err.response?.data?.message || 'Error al guardar cliente');
            }
            throw err;
        }
    };

    return (
        <>
            <div className={classes.headerContainer}>
                <CardHeader
                    onClick={onClick}
                    style={{ cursor: "pointer" }}
                    classes={{
                        root: classes.cardHeaderRoot,
                    }}
                    titleTypographyProps={{
                        className: classes.titleText,
                        variant: "subtitle1"
                    }}
                    subheaderTypographyProps={{
                        className: classes.titleText,
                        variant: "body2"
                    }}
                    avatar={<WhatsAppAvatar src={contact.profilePicUrl} name={contact.name || contact.number} size={40} />}
                    title={formatContactName(contact.name || contact.number || "")}
                    subheader={
                        <>
                            {contact.number ? formatPhoneNumber(contact.number) : ""}
                            {ticket.user && (
                                <>
                                    {contact.number ? " · " : ""}
                                    {i18n.t("messagesList.header.assignedTo")} {ticket.user.name}
                                </>
                            )}
                        </>
                    }
                />
                {!loading && (
                    <div className={classes.buttonContainer}>
                        {orderStatus?.label && (
                            <Tooltip title={orderStatus.order ? "Ver detalle de la orden" : ""}>
                                <Chip
                                    size="small"
                                    icon={<LocalShippingOutlined style={{ fontSize: 14 }} />}
                                    label={getOrderStatusChipLabel(orderStatus)}
                                    onClick={orderStatus.order ? () => setShowOrderDetails(true) : undefined}
                                    className={`${classes.orderChip} ${orderStatus.status === 'active' ? classes.orderChipActive : classes.orderChipFinalized}`}
                                />
                            </Tooltip>
                        )}
                        <IconButton
                            onClick={handleClientButton}
                            className={`${classes.clientButton} ${clientData ? classes.existingClient : classes.newClient}`}
                            title={clientData ? i18n.t("ticketInfo.buttons.editClient") : i18n.t("ticketInfo.buttons.createClient")}
                        >
                            {clientData ? <Person /> : <PersonAddOutlined />}
                        </IconButton>
                    </div>
                )}
            </div>

            {showClientDialog && (
                <ClientEditDialog
                    open={showClientDialog}
                    onClose={() => setShowClientDialog(false)}
                    clientData={clientData || { phone: contact.number, first_name: clientFirstName, last_names: clientLastName }}
                    onSave={handleSaveClient}
                    isNew={!clientData}
                />
            )}

            <OrderDetailsDialog
                open={showOrderDetails && !!orderStatus?.order?.id}
                onClose={() => setShowOrderDetails(false)}
                orderId={orderStatus?.order?.id}
                trackingBaseUrl={trackingBaseUrl}
            />
        </>
    );
};

export default TicketInfo;